import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

// Lead-magnet widgets.
//
// The list endpoint is a POST, not a GET. That is not a typo to be tidied up: the
// route is registered as POST and changing the verb here returns 404.
export function useLeadMagnets() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.post("/s2/api/lead_magnets", {});
      setWidgets(data?.widgets || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load widgets.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = useCallback(async (widgetKey) => {
    await api.del(`/s2/api/lead_magnets/${widgetKey}`);
    setWidgets((prev) => prev.filter((w) => w.widgetKey !== widgetKey));
  }, []);

  const create = useCallback(async (config) => {
    const data = await api.post("/s2/api/widgets", config);
    await load();
    return data?.widgetKey;
  }, [load]);

  // Editing an existing widget. PUT, not POST: the create route mints a new key, so
  // saving an edit through it would leave the original embed live and unchanged on
  // whatever site it is pasted into while the builder showed a second, unused widget.
  const update = useCallback(async (widgetKey, config) => {
    await api.put(`/s2/api/lead_magnets/${widgetKey}`, config);
    await load();
    return widgetKey;
  }, [load]);

  return { widgets, loading, error, reload: load, remove, create, update };
}

// Leads captured by the widgets. Same POST-as-list shape.
export function useLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.post("/s2/api/leads", {});
        if (!cancelled) { setLeads(data?.result || []); setError(""); }
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load leads.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return { leads, loading, error };
}
